import { captureCallTiming } from './callTiming';
import axios from "axios";
import { rewardRequest, rewardConfirmed, rewardStarted, rewardFailed } from "@/lib/rewardBus";
import { getPreview } from "@/lib/employeePreview";
import { toast } from "sonner";

// Production uses a same-origin API bridge so Safari never needs third-party cookies.
// An explicit direct backend URL remains available only for local development.
const RAW_BACKEND_URL = process.env.NODE_ENV === "development"
  ? (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") : "";
const API = `${RAW_BACKEND_URL}/api`;

let careerSession = null;
let careerSessionReady = Promise.resolve();
let careerSessionGeneration = 0;
export function setCareerSession(value) { careerSessionGeneration += 1; careerSession = value; careerSessionReady = Promise.resolve(value); }
export function beginCareerSession(customerId, opportunityId) {
  const generation = ++careerSessionGeneration;
  careerSession = null;
  const pending = api.post('/gamification/call-session', {customer_id: customerId, opportunity_id: opportunityId || null});
  careerSessionReady = pending.then(({data}) => {if (generation === careerSessionGeneration) careerSession = data.session_id; return data.session_id;}).catch(() => {});
  return careerSessionReady;
}

export function captureCareerSession() { return careerSessionReady; }

const api = axios.create({ baseURL: API, timeout: 20000, withCredentials: true });

api.interceptors.request.use(async (cfg) => {
  const safeRead = (cfg.method || "get").toLowerCase() === "get" || cfg.backgroundSafe === true;
  if (safeRead && cfg.timeout === 20000) cfg.timeout = 10000;
  cfg.rewardFX = safeRead ? null : rewardRequest(cfg, getPreview());
  if (cfg.rewardFX) { cfg.headers["X-Reward-Operation"] = cfg.rewardFX.id; rewardStarted(cfg.rewardFX); }
  // Bind to the session at request creation, not whichever card is visible after awaiting.
  const timing = Object.prototype.hasOwnProperty.call(cfg, 'callTiming') ? cfg.callTiming : captureCallTiming();
  if (!safeRead && timing) cfg.headers['X-Call-Context'] = JSON.stringify(timing);
  let capturedSession = careerSession;
  const readyForThisRequest = careerSessionReady;
  if (!String(cfg.url).includes("/gamification/") && !safeRead && !Object.prototype.hasOwnProperty.call(cfg, "careerSessionId")) capturedSession = await readyForThisRequest;
  cfg.headers["X-SM-Request"] = "1";
  const preview = getPreview();
  if (preview) cfg.headers["X-Employee-Preview"] = preview.preview_token;
  const session = Object.prototype.hasOwnProperty.call(cfg, "careerSessionId") ? cfg.careerSessionId : capturedSession;
  if (session && !String(cfg.url).includes("/gamification/")) cfg.headers["X-Gamification-Call"] = session;
  return cfg;
});

api.interceptors.response.use(
  (r) => {
    if (r.data?.status !== "error" && r.data?.ok !== false && r.data?.success !== false) rewardConfirmed(r.config?.rewardFX);
    else rewardFailed(r.config?.rewardFX);
    if ((r.config?.method || "get") !== "get" && r.config?.backgroundSafe !== true && !String(r.config?.url).includes("/gamification/")) {
      setTimeout(() => window.dispatchEvent(new Event("gamification:changed")), 1500);
    }
    return r;
  },
  async (error) => {
    rewardFailed(error.config?.rewardFX);
    const cfg = error.config || {};
    const url = cfg.url || "";
    const isAuthEndpoint = url.includes("/auth/");

    // Session expired -> clean redirect (never on the login/me calls themselves)
    if (error.response && error.response.status === 401 && !isAuthEndpoint && !getPreview()) {
      {
        localStorage.removeItem("ls_token");
        if (!window.location.pathname.includes("/login")) {
          toast.error("Deine Sitzung ist abgelaufen. Bitte erneut anmelden.");
          sessionStorage.setItem("ls_return_to", window.location.pathname + window.location.search);
          setTimeout(() => { window.location.href = "/login"; }, 900);
        }
      }
      return Promise.reject(error);
    }

    // Retry only safe reads (GET) once after a short delay; writes are never retried here.
    const method = (cfg.method || "get").toLowerCase();
    const noResponse = !error.response;
    if ((method === "get" || cfg.backgroundSafe === true) && noResponse && error.code !== "ERR_CANCELED") {
      cfg.__retry = (cfg.__retry || 0) + 1;
      if (cfg.__retry <= 1) {
        await new Promise((res) => setTimeout(res, 750));
        return api(cfg);
      }
    }
    return Promise.reject(error);
  }
);

// Unobtrusive connection state feedback (kept until connection returns)
if (typeof window !== "undefined") {
  window.addEventListener("offline", () => {
    toast.error("Verbindung unterbrochen. Wir versuchen automatisch erneut zu verbinden.", {
      id: "net-status", duration: Infinity,
    });
  });
  window.addEventListener("online", () => {
    toast.dismiss("net-status");
    toast.success("Verbindung wiederhergestellt.");
  });
}

export function apiError(e) {
  if (e && e.code === "ECONNABORTED") return "Die Antwort dauert länger als erwartet. Bitte erneut versuchen.";
  if (e && !e.response) return "Verbindung aktuell nicht verfügbar. Bitte erneut versuchen.";
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  if (e?.response?.status >= 500) return "Die Aktion konnte gerade nicht abgeschlossen werden. Bitte erneut versuchen.";
  return e?.message || "Ein Fehler ist aufgetreten.";
}

export default api;
export { API };

export function idemKey() {
  try { return crypto.randomUUID(); } catch (_) { return `${Date.now()}-${Math.random().toString(36).slice(2)}`; }
}
